import { supabase } from "../supabaseClient";

export const DEFAULT_BOWLERS_PER_LANE = 4;
export const DEFAULT_LANES_USED = 10;
export const MIN_BOWLERS_PER_LANE = 1;
export const MAX_BOWLERS_PER_LANE = 8;

const asPositiveInt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return 0;
  return Math.floor(n);
};

export const squadLanesUsed = (squad, settings) =>
  asPositiveInt(squad?.lanesUsed) ||
  asPositiveInt(settings?.lanesUsed) ||
  DEFAULT_LANES_USED;

export const squadBowlersPerLane = (settings) => {
  const n = asPositiveInt(settings?.bowlersPerLane);
  return n || DEFAULT_BOWLERS_PER_LANE;
};

export const squadMaxBowlers = (squad, settings) => {
  const lanes = squadLanesUsed(squad, settings);
  const perLane = asPositiveInt(settings?.bowlersPerLane);
  if (lanes < 1 || perLane < 1) return 0;
  return lanes * perLane;
};

export const eventSpotCount = (maxBowlers, teamSize) => {
  const people = asPositiveInt(maxBowlers);
  const size = asPositiveInt(teamSize);
  if (people < 1 || size < 1) return 0;
  return Math.floor(people / size);
};

const eventIdsOnSquad = (
  squad,
  eventsBySquad = {},
  extrasBySquad = {}
) => {
  if (Array.isArray(squad?.eventIds)) return squad.eventIds.filter(Boolean);
  return (
    (squad?.id && (eventsBySquad[squad.id] || extrasBySquad[squad.id])) || []
  ).filter(Boolean);
};

export const signupOccupiesSquad = (
  event,
  squad,
  { events = [], eventsBySquad = {}, extrasBySquad = {} } = {}
) => {
  if (!event || Number(event.bowlerQuantity) !== 1) return true;
  const ids = new Set(eventIdsOnSquad(squad, eventsBySquad, extrasBySquad));
  return !(events || []).some(
    (row) => ids.has(row.id) && Number(row.bowlerQuantity) === 2
  );
};

export const occupyingTeamSize = (
  event,
  squad,
  { events = [], eventsBySquad = {}, extrasBySquad = {} } = {}
) => {
  const ids = new Set(eventIdsOnSquad(squad, eventsBySquad, extrasBySquad));
  const onSquad = (events || []).filter((row) => ids.has(row.id));
  const biggest = onSquad.reduce(
    (best, row) =>
      Number(row.bowlerQuantity || 0) > Number(best?.bowlerQuantity || 0)
        ? row
        : best,
    null
  );
  return (
    asPositiveInt(biggest?.bowlerQuantity) ||
    asPositiveInt(event?.bowlerQuantity)
  );
};

export const squadSpotCap = (squad, settings, event, options = {}) =>
  eventSpotCount(
    squadMaxBowlers(squad, settings),
    occupyingTeamSize(event, squad, options)
  );

export const squadSpotsLeft = (occupied, maxSpots) => {
  if (!maxSpots || maxSpots < 1) return null;
  return Math.max(0, maxSpots - (occupied || 0));
};

export const isSquadFull = (occupied, maxSpots) => {
  if (!maxSpots || maxSpots < 1) return false;
  return (occupied || 0) >= maxSpots;
};

export const occupancyBySquadId = (rows = []) => {
  const map = {};
  rows.forEach((row) => {
    if (row?.squad_id) {
      map[row.squad_id] = row.occupied || 0;
    }
  });
  return map;
};

export const fetchSquadWalkUpCap = async ({
  squad,
  event,
  settings,
  events = [],
  eventsBySquad = {},
  client = supabase,
}) => {
  if (!squad?.id) {
    return { occupied: 0, max: 0, waitlistCount: 0, full: false };
  }
  const [{ data: occRows }, waitlist] = await Promise.all([
    client.rpc("get_squad_occupancy", { p_squad_ids: [squad.id] }),
    client
      .from("tournamentSignups")
      .select("id", { count: "exact", head: true })
      .eq("squadID", squad.id)
      .eq("status", "waitlisted"),
  ]);
  const occupied = occupancyBySquadId(occRows || [])[squad.id] || 0;
  const max = squadSpotCap(squad, settings, event, { events, eventsBySquad });
  const waitlistCount = waitlist.error ? 0 : waitlist.count || 0;
  return {
    occupied,
    max,
    waitlistCount,
    full: isSquadFull(occupied, max),
  };
};

export const signupCapacityPreview = (settings, events = []) => {
  const lanes = asPositiveInt(settings?.lanesUsed) || DEFAULT_LANES_USED;
  const perLane =
    asPositiveInt(settings?.bowlersPerLane) || DEFAULT_BOWLERS_PER_LANE;
  const bowlers = lanes >= 1 && perLane >= 1 ? lanes * perLane : 0;
  const lines = (events || [])
    .filter((event) => asPositiveInt(event?.bowlerQuantity) >= 1)
    .map((event) => ({
      eventName: event.eventName || "Event",
      spots: eventSpotCount(bowlers, event.bowlerQuantity),
    }));
  return { lanes, perLane, bowlers, lines };
};

export const formatSignupCapacityPreview = (preview) => {
  if (!preview?.bowlers) return "Set max lanes to see spots.";
  const head = `${preview.lanes} lanes × ${preview.perLane} bowlers per lane = ${preview.bowlers} bowlers`;
  if (!preview.lines?.length) return `${head}.`;
  const spots = preview.lines
    .map((line) => `${line.eventName}: ${line.spots} spots`)
    .join(". ");
  return `${head}. ${spots}.`;
};
